/**
 * components/landing/ai-platform/sections/Hero.jsx — §4.1.
 *
 * Introduces the AI (not sells).  The visual is an animated neural
 * network with signals flowing from a central brain outward to
 * the platform modules — saying “AI is wired into everything”.
 */

import React from 'react'
import { PlayCircle, Sparkles, Bot, LineChart, Wand2, BookOpen, GraduationCap, PieChart, UsersRound, Building2, Cpu } from 'lucide-react'
import { Container, Eyebrow, GradientButton, GhostButton } from '@/components/landing/design-system'

const MODULES = [
  { i: LineChart,     angle:   0, label: 'Terminal',  tone: '#22c55e' },
  { i: Wand2,         angle:  45, label: 'Strategy',  tone: '#a855f7' },
  { i: BookOpen,      angle:  90, label: 'KB',        tone: '#a855f7' },
  { i: GraduationCap, angle: 135, label: 'Academy',   tone: '#a855f7' },
  { i: PieChart,      angle: 180, label: 'Analytics', tone: '#3b82f6' },
  { i: UsersRound,    angle: 225, label: 'CRM',       tone: '#3b82f6' },
  { i: Building2,     angle: 270, label: 'WL',        tone: '#22d3ee' },
  { i: Cpu,           angle: 315, label: 'Automation',tone: '#FFC107' },
]

export default function Hero() {
  const R = 175, C = 260
  return (
    <section className="relative overflow-hidden pt-28 md:pt-36 pb-16 md:pb-24">
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.5]"
        style={{ background: 'radial-gradient(60% 45% at 50% 20%, rgba(168,85,247,0.12), transparent 70%)' }} />
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(to_bottom,rgba(0,0,0,0)_0%,rgba(0,0,0,0.4)_70%,rgba(0,0,0,1)_100%)]" />
      <Container size="wide">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 flex flex-col gap-6">
            <Eyebrow>The AI Platform</Eyebrow>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.02]">
              Enterprise <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">AI</span>, built for trading businesses.
            </h1>
            <p className="text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed">
              Not a chatbot. A complete AI infrastructure — RAG, prompt orchestration, contextual intelligence and workflow automation — wired into every module of the Kalki Ai Cloud platform.
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-3 pt-2">
              <GradientButton href="#ai-mentor" size="lg">Experience the AI</GradientButton>
              <GhostButton href="/get-started" size="lg" trailingIcon>
                <PlayCircle className="h-4 w-4" /> Book a demo
              </GhostButton>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {['Grounded retrieval', 'Role-scoped memory', 'Auditable actions', 'Model-agnostic'].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11.5px] font-semibold text-zinc-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-gold" /> {t}
                </span>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative aspect-square w-full max-w-[540px] mx-auto">
              <svg viewBox="0 0 520 520" className="w-full h-full">
                <defs>
                  <radialGradient id="aicore" cx="50%" cy="50%" r="60%">
                    <stop offset="0%"  stopColor="#FBF6E9" stopOpacity="0.95" />
                    <stop offset="40%" stopColor="#a855f7" stopOpacity="0.85" />
                    <stop offset="100%" stopColor="#2b0e5c" stopOpacity="1" />
                  </radialGradient>
                  <radialGradient id="aihalo" cx="50%" cy="50%" r="60%">
                    <stop offset="0%"   stopColor="#a855f7" stopOpacity="0.55" />
                    <stop offset="100%" stopColor="#a855f7" stopOpacity="0" />
                  </radialGradient>
                </defs>

                <circle cx={C} cy={C} r={R + 40} stroke="rgba(255,255,255,0.05)" fill="none" />
                <circle cx={C} cy={C} r={R}      stroke="rgba(168,85,247,0.15)" strokeDasharray="2 8" fill="none" />

                {/* connection lines with animated packets */}
                {MODULES.map((m, i) => {
                  const a = (m.angle - 90) * (Math.PI / 180)
                  const x = C + Math.cos(a) * R
                  const y = C + Math.sin(a) * R
                  const id = `aiwire-${i}`
                  return (
                    <g key={i}>
                      <path id={id} d={`M ${C} ${C} L ${x} ${y}`} stroke={`${m.tone}66`} strokeWidth="1.2" strokeDasharray="3 6" fill="none" className="animate-brand-dash" />
                      <circle r="3" fill={m.tone}>
                        <animateMotion dur={`${3 + (i % 3) * 0.6}s`} repeatCount="indefinite">
                          <mpath href={`#${id}`} />
                        </animateMotion>
                      </circle>
                    </g>
                  )
                })}

                {/* core halo */}
                <circle cx={C} cy={C} r="110" fill="url(#aihalo)" opacity="0.7" className="animate-brand-pulse" style={{ transformOrigin: `${C}px ${C}px` }} />
                <circle cx={C} cy={C} r="58"  fill="url(#aicore)" />
                <text  x={C} y={C - 2}  textAnchor="middle" fontSize="14" fontWeight="800" fill="#0a0a0a" style={{ letterSpacing: '0.06em' }}>AI</text>
                <text  x={C} y={C + 16} textAnchor="middle" fontSize="9"  fontWeight="700" fill="#0a0a0a" style={{ letterSpacing: '0.28em' }}>CORE</text>

                {/* orbiting modules */}
                {MODULES.map((m, i) => {
                  const a = (m.angle - 90) * (Math.PI / 180)
                  const x = C + Math.cos(a) * R
                  const y = C + Math.sin(a) * R
                  return (
                    <g key={`n${i}`} transform={`translate(${x} ${y})`}>
                      <circle r="28" fill="rgba(10,10,12,0.9)" stroke={m.tone} strokeOpacity="0.55" />
                      <circle r="25" fill={`${m.tone}12`} />
                      <foreignObject x="-13" y="-13" width="26" height="26">
                        <div className="flex items-center justify-center w-full h-full" style={{ color: m.tone }}>
                          <m.i strokeWidth={1.8} style={{ width: 14, height: 14 }} />
                        </div>
                      </foreignObject>
                      <text y="44" textAnchor="middle" fontSize="10.5" fontWeight="600" fill="rgba(255,255,255,0.85)">{m.label}</text>
                    </g>
                  )
                })}
              </svg>
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}
